/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useMemo} from 'react'
import {FormattedNumber, IntlProvider} from 'react-intl'
import clsx from 'clsx'
import './DashboardScoreCards.scss'

type Metric = 'revenue' | 'transaction' | 'item' | 'customer'

type CardConfig = {
  key: Metric
  title: string
  value: number
  previousValue?: number
  isCurrency: boolean
}

type Props = {
  revenue?: number
  transaction?: number
  item?: number
  customer?: number
  previousRevenue?: number
  previousTransaction?: number
  previousItem?: number
  previousCustomer?: number
  isLoading?: boolean
}

function percentChange(current: number, previous: number): number | null {
  if (previous === 0) return current > 0 ? 100 : null
  return ((current - previous) / previous) * 100
}

const DashboardScoreCards: FC<Props> = ({
  revenue = 0,
  transaction = 0,
  item = 0,
  customer = 0,
  previousRevenue,
  previousTransaction,
  previousItem,
  previousCustomer,
  isLoading = false,
}) => {
  const cards: CardConfig[] = useMemo(
    () => [
      {
        key: 'revenue',
        title: 'Sales',
        value: revenue,
        previousValue: previousRevenue,
        isCurrency: true,
      },
      {
        key: 'transaction',
        title: 'Transaction',
        value: transaction,
        previousValue: previousTransaction,
        isCurrency: false,
      },
      {
        key: 'item',
        title: 'Item',
        value: item,
        previousValue: previousItem,
        isCurrency: false,
      },
      {
        key: 'customer',
        title: 'Customer',
        value: customer,
        previousValue: previousCustomer,
        isCurrency: false,
      },
    ],
    [
      revenue,
      transaction,
      item,
      customer,
      previousRevenue,
      previousTransaction,
      previousItem,
      previousCustomer,
    ]
  )

  return (
    <div className='row g-5 g-xl-4 mb-5 mb-xl-6'>
      {cards.map((c) => {
        const prev = c.previousValue ?? 0
        const hasComparison = typeof c.previousValue === 'number'
        const pct = hasComparison ? percentChange(c.value, prev) : null

        return (
          <div key={c.key} className='col-6 col-lg-3'>
            <div className='card h-100 border-0 shadow-sm dashboard-score-card'>
              <div className='card-header border-0 pt-5'>
                <div className='d-flex align-items-start justify-content-between gap-2'>
                  <h3 className='card-title align-items-start flex-column mb-0'>
                    <span className='card-label fw-bold text-gray-900'>{c.title}</span>
                    <span className='text-muted mt-1 fw-semibold fs-7'>This Month</span>
                  </h3>
                  {hasComparison && pct !== null && (
                    <span
                      className={clsx(
                        'dashboard-score-card__badge',
                        pct >= 0
                          ? 'dashboard-score-card__badge--up'
                          : 'dashboard-score-card__badge--down'
                      )}
                    >
                      {`${pct >= 0 ? '↑' : '↓'} ${Math.abs(pct).toFixed(1)}%`}
                    </span>
                  )}
                </div>
              </div>
              <div className='card-body pt-0 pb-4 px-5'>
                <div className='dashboard-score-card__value-wrap'>
                  {isLoading ? (
                    <span className='dashboard-score-card__value'>...</span>
                  ) : (
                    <>
                      {c.isCurrency && (
                        <span className='dashboard-score-card__currency'>Rp.</span>
                      )}
                      <span className='dashboard-score-card__value'>
                        <IntlProvider locale='id'>
                          <FormattedNumber
                            value={c.value}
                            minimumFractionDigits={0}
                            maximumFractionDigits={0}
                          />
                        </IntlProvider>
                      </span>
                    </>
                  )}
                </div>
                <div className='dashboard-score-card__last-month'>
                  Last month: {hasComparison ? (c.isCurrency ? 'Rp. ' : '') : ''}
                  {hasComparison ? (
                    <IntlProvider locale='id'>
                      <FormattedNumber
                        value={prev}
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                      />
                    </IntlProvider>
                  ) : (
                    '—'
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export {DashboardScoreCards}
